import { useMemo } from 'react';
import useGraphqlQuery from '../../../hooks/useGraphqlQuery';
import { LINEAGE_HIGHLIGHT_COLUMNS } from '../../../constants/queryKeys';
import { getColumnLineageQuery } from '../api';
import type { ApiLineage, HopLimit, LineageQueryResponse } from '../types';
import { parseLineageResponse } from '../utils/lineageApiParsing';

type Props = {
  enabled: boolean;
  assetIds?: string[];
  highlightedColumnId?: string;
  hopLimit?: HopLimit;
  perParentColumnsLimit?: number;
  hasColumnFilter: boolean;
};

export const useHighlightColumnLineage = ({
  enabled,
  assetIds,
  highlightedColumnId,
  hopLimit,
  perParentColumnsLimit,
  hasColumnFilter,
}: Props) => {
  const { data: gqlLineageData, isFetching: isHighlightLineageFetching } =
    useGraphqlQuery<LineageQueryResponse>({
      queryKey: [
        LINEAGE_HIGHLIGHT_COLUMNS,
        highlightedColumnId,
        assetIds,
        hopLimit?.downstream ?? 1,
        hopLimit?.upstream ?? 1,
        perParentColumnsLimit,
      ],
      query: getColumnLineageQuery,
      variables: {
        assetIds: assetIds ?? [],
        columnIds: highlightedColumnId ? [highlightedColumnId] : null,
        downstreamHops: hopLimit?.downstream ?? 1,
        upstreamHops: hopLimit?.upstream ?? 1,
        perParentColumnsLimit,
      },
      enabled:
        enabled &&
        !!highlightedColumnId &&
        !hasColumnFilter &&
        !!(assetIds?.length || highlightedColumnId),
    });

  const highlightColumnSubgraph: ApiLineage | null = useMemo(
    () => parseLineageResponse(gqlLineageData),
    [gqlLineageData],
  );

  return {
    highlightColumnSubgraph,
    isHighlightLineageFetching,
  };
};

export default useHighlightColumnLineage;
